import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'

/**
 * Elements a Tab sequence should stop on. Mirrors the common focus-trap
 * candidate list (a[href], form controls, explicit tabindex) — not
 * exhaustive (no visibility/inert checks), matching this hook's minimal scope.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
}

/**
 * Stack of currently-trapped dialog containers, most-recently-mounted last.
 * The trap listens on `document` (capture), so two concurrently-mounted imperative
 * dialogs (e.g. an `alertDialog` stacked over an awaiting `confirmDialog`) would
 * otherwise BOTH handle a Tab and fight over focus. Only the top-of-stack dialog's
 * handler acts; the ones beneath no-op — preserving the mutually-inert behavior the
 * old container-scoped listeners had for stacked dialogs.
 */
const activeTrapStack: HTMLElement[] = []

/**
 * useDialogFocus — minimal modal focus trap + restore (#5263, F-a11y-07).
 *
 * Captures the element that had focus BEFORE this dialog appeared, moves
 * focus into `ref`'s container (its first focusable descendant, or the
 * container itself via a `tabindex="-1"` fallback if it has none), traps
 * Tab/Shift+Tab inside it while mounted, and restores focus on unmount.
 *
 * The capture happens during RENDER (a lazy ref-init), not inside an effect:
 * an in-container `autoFocus` (e.g. ConfirmDialog's `autoFocus={danger}`)
 * runs during React's commit — before ANY effect, including a
 * `useLayoutEffect` here — so capturing in an effect would read the
 * `autoFocus` target instead of the real trigger. Render always runs before
 * commit, so it's the only point that reliably sees pre-dialog focus.
 *
 * The initial-focus MOVE (in the mount effect below) still skips itself if
 * the container already contains `document.activeElement` — i.e. an
 * `autoFocus` already ran — so this hook only adds the trap + restore on
 * top, it never fights an autoFocus that already placed focus correctly.
 */
export function useDialogFocus(ref: RefObject<HTMLElement | null>): void {
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  if (previouslyFocusedRef.current === null) {
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null
  }

  useEffect(() => {
    const container = ref.current
    if (!container) return

    if (!container.contains(document.activeElement)) {
      const [first] = getFocusable(container)
      if (first) {
        first.focus()
      } else {
        if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1')
        container.focus()
      }
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      // Only the frontmost trapped dialog governs Tab — stacked dialogs beneath
      // it defer, so they don't both yank focus on the same keypress.
      if (activeTrapStack[activeTrapStack.length - 1] !== container) return
      const focusables = getFocusable(container)
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const isInside = container.contains(document.activeElement)

      if (e.shiftKey) {
        if (!isInside || document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (!isInside || document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    // Listen on `document` (capture), not the container: once focus escapes to
    // `<body>` (e.g. a click on the dialog's non-focusable title text), a keydown
    // no longer propagates through the container, so a container-scoped listener
    // could never run its own `!isInside` recovery — Tab would walk into the page
    // behind the modal. Document capture sees every Tab while the dialog is mounted,
    // so the wrap-back logic actually fires. (#5263 follow-up.)
    // NOTE: capture phase means this preempts any in-dialog element that wants Tab
    // for itself; harmless while the sole consumer is ConfirmDialog (buttons only),
    // but a future consumer with its own Tab handling should account for it.
    activeTrapStack.push(container)
    document.addEventListener('keydown', handleKeyDown, true)
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      const idx = activeTrapStack.lastIndexOf(container)
      if (idx !== -1) activeTrapStack.splice(idx, 1)
      const previouslyFocused = previouslyFocusedRef.current
      if (previouslyFocused && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus()
      }
    }
  }, [ref])
}
